import * as styles from "./PdfViewerThumbnails.css";

import { clsx } from "clsx";
import {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import { useScroll } from "@embedpdf/plugin-scroll/react";
import {
  ThumbImg,
  useThumbnailCapability,
} from "@embedpdf/plugin-thumbnail/react";
import {
  ScrollArea,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import { useLingui } from "@lingui/react/macro";

import { usePdfViewerContext } from "./PdfViewer.context";

const noop = () => () => {};

export function PdfViewerThumbnails(): JSX.Element {
  const { t } = useLingui();

  const { documentId } = usePdfViewerContext();
  const { provides } = useThumbnailCapability();

  const { provides: scroll, state } = useScroll(documentId);

  const thumbnails = useMemo(
    () => provides?.forDocument(documentId) ?? null,
    [provides, documentId],
  );

  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);

  const thumbWindow = useSyncExternalStore(
    useCallback(
      (onChange: () => void) => thumbnails?.onWindow(onChange) ?? noop(),
      [thumbnails],
    ),
    useCallback(() => thumbnails?.getWindow() ?? null, [thumbnails]),
  );

  useEffect(() => {
    if (thumbnails === null || viewport === null) {
      return;
    }

    const measure = () =>
      thumbnails.updateWindow(viewport.scrollTop, viewport.clientHeight);

    measure();

    const observer = new ResizeObserver(measure);

    observer.observe(viewport);

    viewport.addEventListener("scroll", measure, { passive: true });

    return () => {
      observer.disconnect();
      viewport.removeEventListener("scroll", measure);
    };
  }, [thumbnails, viewport, thumbWindow?.totalHeight]);

  useEffect(() => {
    if (thumbnails === null || viewport === null) {
      return;
    }

    return thumbnails.onScrollTo(({ top, behavior }) =>
      viewport.scrollTo({ top, behavior }),
    );
  }, [thumbnails, viewport]);

  return (
    <aside
      className={styles.aside}
      aria-label={t`Page thumbnails`}
      data-slot="pdf-viewer-thumbnails"
    >
      <ScrollArea className={styles.scroller}>
        <ScrollAreaViewport ref={setViewport} className={styles.viewport}>
          <div
            className={styles.list}
            style={{ height: thumbWindow?.totalHeight ?? 0 }}
          >
            {thumbWindow?.items.map((meta) => {
              const pageNumber = meta.pageIndex + 1;
              const active = pageNumber === state.currentPage;

              return (
                <button
                  key={meta.pageIndex}
                  type="button"
                  className={clsx(styles.row, active && styles.rowActive)}
                  style={{ top: meta.top, height: meta.wrapperHeight }}
                  aria-current={active ? "page" : undefined}
                  onClick={() => scroll?.scrollToPage({ pageNumber })}
                >
                  <span
                    className={styles.paper}
                    style={{ width: meta.width, height: meta.height }}
                  >
                    <ThumbImg
                      documentId={documentId}
                      meta={meta}
                      className={styles.image}
                    />
                  </span>

                  <span
                    className={styles.label}
                    style={{ height: meta.labelHeight }}
                  >
                    {pageNumber}
                  </span>
                </button>
              );
            })}
          </div>
        </ScrollAreaViewport>

        <ScrollAreaScrollbar orientation="vertical">
          <ScrollAreaThumb />
        </ScrollAreaScrollbar>
      </ScrollArea>
    </aside>
  );
}
